import type { GameData } from "@/app/games/(details)/types";
import { Action } from "@/context/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { cn } from "@/lib/utils";
import { isDesktop } from "@/utils/browser";
import { useCallback, useEffect, useRef, useState } from "react";
import { ImageBackground } from "../ui/custom/image-background";

const HAS_PLAYED_INTROS_KEY = "hasPlayedIntros";

type HasPlayedIntros = Record<string, boolean>;

interface VideoIntroProps {
  gameData: GameData;
  children: React.ReactNode;
}

const VideoIntroEnabled = ({ gameData, children }: VideoIntroProps) => {
  const [prepContent, setPrepContent] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const initialViewedRef = useRef(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { track } = useGamePageAnalytics();

  useEffect(() => {
    // add overflow hidden to the body
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (initialViewedRef.current) return;
    initialViewedRef.current = true;

    track({
      action: Action.VIEWED,
      extras: {
        control: "start",
        section: GamePageSection.VIDEO_INTRO,
      },
    });
  }, [track]);

  const handleVideoEnd = useCallback(() => setVideoEnded(true), []);

  const handleCanPlayThrough = useCallback(() => setPrepContent(true), []);

  const revealContent = useCallback(() => {
    setShowContent(true);

    if (gameData.details.slug) {
      try {
        const existingIntros: HasPlayedIntros = JSON.parse(
          localStorage.getItem(HAS_PLAYED_INTROS_KEY) || "{}",
        );
        localStorage.setItem(
          HAS_PLAYED_INTROS_KEY,
          JSON.stringify({ ...existingIntros, [gameData.details.slug]: true }),
        );
      } catch (_error) {
        localStorage.setItem(
          HAS_PLAYED_INTROS_KEY,
          JSON.stringify({ [gameData.details.slug]: true }),
        );
      }
    }

    track({
      action: Action.VIEWED,
      extras: {
        control: "end",
        section: GamePageSection.VIDEO_INTRO,
      },
    });

    setTimeout(() => {
      document.body.style.overflow = "";
    }, 500);
  }, [track, gameData.details.slug]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (video.currentTime >= video.duration - 0.5) {
        revealContent();
        video.removeEventListener("timeupdate", handleTimeUpdate);
      }
    };
    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, [revealContent]);

  return (
    <>
      {/* Video overlay - fades out when video ends, revealing static background behind */}
      <video
        ref={videoRef}
        src={gameData.gameContent?.video_splash_url}
        muted
        onCanPlayThrough={handleCanPlayThrough}
        autoPlay
        preload="auto"
        onEnded={handleVideoEnd}
        className={cn(
          "fixed inset-0 w-screen min-h-lvh object-cover transition-opacity duration-500",
          videoEnded ? "opacity-0 pointer-events-none" : "opacity-100",
          showContent ? "z-0" : "z-10",
        )}
        playsInline
      />

      {/* Page Content */}
      {prepContent ? (
        <div
          className={cn(
            "flex w-full h-full transition-all duration-500",
            showContent ? "opacity-100" : "opacity-5",
          )}
        >
          {children}
        </div>
      ) : null}
    </>
  );
};

export const VideoIntro = ({ children, gameData }: VideoIntroProps) => {
  const [hasPlayedIntro, setHasPlayedIntro] = useState<boolean | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!gameData.details.slug) return;

    try {
      setHasPlayedIntro(
        JSON.parse(localStorage.getItem(HAS_PLAYED_INTROS_KEY) || "{}")[
          gameData.details.slug
        ] ?? false,
      );
    } catch (_error) {
      setHasPlayedIntro(false);
    }
  }, [gameData.details.slug]);

  const bgUrl = gameData.details.background_url;

  // Video intro will only play if: desktop device, user hasn't seen it, and video exists
  const shouldShowIntro =
    hasPlayedIntro === false &&
    isDesktop() &&
    !!gameData.gameContent?.video_splash_url;

  return (
    <div className="flex w-full h-full">
      {bgUrl ? (
        <ImageBackground
          staticImageUrl={bgUrl}
          backdropClassName="bg-black/50"
          quality={40}
          priority
        />
      ) : null}
      {shouldShowIntro ? (
        <VideoIntroEnabled gameData={gameData}>{children}</VideoIntroEnabled>
      ) : (
        children
      )}
    </div>
  );
};
